/**
 * Frontend Environment Variable Validation
 * Validates that all required React environment variables are present
 * Note: React requires all env vars to start with REACT_APP_
 */

/* eslint-disable no-console */
// Console statements are intentional here for environment validation at startup

interface FrontendEnvironmentConfig {
  REACT_APP_API_URL: string;
  REACT_APP_SOCKET_URL?: string;
  REACT_APP_APP_NAME?: string;
  REACT_APP_VERSION?: string;
  NODE_ENV: string;
}

/**
 * Required environment variables that must be present
 */
const REQUIRED_ENV_VARS = ['REACT_APP_API_URL'] as const;

/**
 * Optional environment variables with defaults
 */
const OPTIONAL_ENV_VARS = {
  REACT_APP_SOCKET_URL: '',
  REACT_APP_APP_NAME: 'cmndf',
  REACT_APP_VERSION: '1.0.0',
  NODE_ENV: 'development',
} as const;

/**
 * Validates environment variables and returns typed config
 * Logs warnings if required variables are missing (doesn't throw in browser)
 */
export function validateFrontendEnv(): FrontendEnvironmentConfig {
  const missing: string[] = [];
  const warnings: string[] = [];

  // Check required variables
  for (const varName of REQUIRED_ENV_VARS) {
    if (!process.env[varName]) {
      missing.push(varName);
    }
  }

  // Log errors if any required variables are missing
  if (missing.length > 0) {
    console.error('⚠️ Missing required environment variables:');
    missing.forEach(varName => {
      console.error(`   - ${varName}`);
    });
    console.error('\n💡 Create a .env file in the frontend directory with:');
    console.error('   REACT_APP_API_URL=http://localhost:5000/api');
    console.error('   See .env.example for full template.\n');
    
    // In development, provide a default to allow the app to run
    if (process.env.NODE_ENV === 'development') {
      console.warn('🔧 Using default API URL for development: http://localhost:5000/api');
      warnings.push('Using default API URL - this may cause connection issues');
    }
  }

  // Build config object
  const config: any = {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
    NODE_ENV: process.env.NODE_ENV || 'development',
  };

  // Add optional vars with defaults
  for (const [varName, defaultValue] of Object.entries(OPTIONAL_ENV_VARS)) {
    if (varName === 'NODE_ENV') continue; // Already handled
    config[varName] = process.env[varName] || defaultValue;
  }

  // Validate API URL format (allow relative URLs for single-service deployment)
  if (config.REACT_APP_API_URL) {
    const isRelativeUrl = config.REACT_APP_API_URL.startsWith('/');
    if (!isRelativeUrl) {
      try {
        new URL(config.REACT_APP_API_URL);
      } catch (error) {
        console.error(`❌ Invalid REACT_APP_API_URL: ${config.REACT_APP_API_URL}`);
        console.error('   Must be a valid URL (e.g., http://localhost:5000/api) or relative path (e.g., /api)');
      }
    }
  }

  // Production-specific validation
  if (config.NODE_ENV === 'production') {
    if (!config.REACT_APP_API_URL || config.REACT_APP_API_URL.includes('localhost')) {
      console.warn('⚠️ WARNING: Using localhost API URL in production build!');
      console.warn('   Update REACT_APP_API_URL to your production backend URL');
    }
  }

  // Log warnings if any
  if (warnings.length > 0) {
    warnings.forEach(warning => {
      console.warn(`⚠️  ${warning}`);
    });
  }

  // Success message (only in development)
  if (config.NODE_ENV === 'development' && missing.length === 0) {
    console.log('✅ Frontend environment variables validated');
  }

  return config as FrontendEnvironmentConfig;
}

/**
 * Get validated environment config
 * Call this once at app startup
 */
export const env = validateFrontendEnv();

/**
 * Helper function to get API URL
 */
export const getApiUrl = (): string => env.REACT_APP_API_URL;

const INVALID_SOCKET_HOSTS = new Set(['', 'https', 'http']);

/**
 * Origin to pass to Socket.IO (must be a real hostname, not a bare "https" typo).
 */
function socketOriginFromHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) return null;
  try {
    const base =
      typeof window !== 'undefined' && window.location?.href
        ? window.location.href
        : 'http://localhost';
    const parsed = new URL(trimmed, base);
    if (INVALID_SOCKET_HOSTS.has(parsed.hostname)) return null;
    return parsed.origin;
  } catch {
    return null;
  }
}

/**
 * Helper function to get Socket URL (falls back to API origin, then page origin).
 * Uses the URL API instead of string replace so malformed values cannot yield `io("https")`
 * (which connects to host "https" → wss://https/socket.io/...).
 */
export const getSocketUrl = (): string => {
  const explicit = env.REACT_APP_SOCKET_URL?.trim();
  if (explicit) {
    const origin = socketOriginFromHref(explicit);
    if (origin) return origin;
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[getSocketUrl] REACT_APP_SOCKET_URL is invalid; falling back to API or page origin:',
        explicit
      );
    }
  }

  const api = env.REACT_APP_API_URL?.trim();
  if (api) {
    const fromApi = socketOriginFromHref(api);
    if (fromApi) return fromApi;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

/**
 * Helper to check if we're in production
 */
export const isProduction = (): boolean => env.NODE_ENV === 'production';

/**
 * Helper to check if we're in development
 */
export const isDevelopment = (): boolean => env.NODE_ENV === 'development';

